import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { SaleOffer } from '../../api/SalesAPI';
import PriceField from '../PriceField/PriceField';
import SaleOfferControl from '../SaleOfferControl/SaleOfferControl';
import Tooltip from '../Tooltip/Tooltip';
import { applyOffer, formatOfferSummary, isOfferPresent } from '../../utils/saleLineItems';

type Props = {
  itemName: string;
  productVariant?: string;
  testId?: string;
  price: number;
  currency: string;
  currencies: string[];
  onPriceChange: (price: number) => void;
  onCurrencyChange: (currency: string) => void;
  offer?: SaleOffer;
  baseAmount: number;
  onOfferChange: (offer: SaleOffer | undefined) => void;
  isOfferDisabled?: boolean;
  disabledHint?: string;
  isOfferExpanded?: boolean;
  onOfferExpandedChange?: (expanded: boolean) => void;
  onRemoveItem?: () => void;
  removeItemAriaLabel?: string;
  isDisabled?: boolean;
  /** When false, discount controls are hidden (e.g. primary line before productId is set). */
  showOfferControls?: boolean;
  selectedItemSrLabel?: string;
  /** Primary line shows variant inline with em dash; additional items show on separate line. */
  variantDisplay?: 'inline' | 'block';
};

function SaleItemLine({
  itemName,
  productVariant,
  testId,
  price,
  currency,
  currencies,
  onPriceChange,
  onCurrencyChange,
  offer,
  baseAmount,
  onOfferChange,
  isOfferDisabled = false,
  disabledHint,
  isOfferExpanded,
  onOfferExpandedChange,
  onRemoveItem,
  removeItemAriaLabel,
  isDisabled = false,
  showOfferControls = true,
  selectedItemSrLabel,
  variantDisplay = 'block',
}: Props) {
  const { t } = useTranslation();
  const [internalOfferExpanded, setInternalOfferExpanded] = useState(false);
  const isOfferExpandedControlled = isOfferExpanded !== undefined;
  const offerExpanded = isOfferExpandedControlled ? isOfferExpanded : internalOfferExpanded;

  const handleOfferExpandedChange = (expanded: boolean) => {
    if (!isOfferExpandedControlled) setInternalOfferExpanded(expanded);
    onOfferExpandedChange?.(expanded);
  };

  const showOfferSummary = isOfferPresent(offer) && !offerExpanded;
  const offerDisabled = isDisabled || isOfferDisabled;

  return (
    <li className="px-3 py-2.5 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          {selectedItemSrLabel && (
            <span className="sr-only">{selectedItemSrLabel}</span>
          )}
          <div className="font-medium text-gray-900 break-words leading-snug">
            {itemName}
            {productVariant && variantDisplay === 'inline' && (
              <span className="ml-1.5 font-normal text-gray-600">— {productVariant}</span>
            )}
          </div>
          {productVariant && variantDisplay === 'block' && (
            <div className="text-gray-600">{productVariant}</div>
          )}
          {showOfferSummary && offer && (
            <p className="mt-0.5 text-xs text-gray-500">
              {formatOfferSummary(offer, currency, baseAmount)}
              <span className="mx-1">·</span>
              <span className="font-semibold text-gray-900">
                {applyOffer(baseAmount, offer).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                {currency}
              </span>
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="w-44">
            <PriceField
              label=""
              currency={currency}
              price={price}
              currencies={currencies}
              onCurrencyChange={onCurrencyChange}
              onPriceChange={onPriceChange}
              isDisabled={isDisabled}
            />
          </div>
          {showOfferControls && !isDisabled && (
            <SaleOfferControl
              variant="rowAction"
              actionOnly
              offer={offer}
              baseAmount={baseAmount}
              currency={currency}
              onChange={onOfferChange}
              isDisabled={offerDisabled}
              disabledHint={disabledHint}
              isExpanded={offerExpanded}
              onExpandedChange={handleOfferExpandedChange}
            />
          )}
          {!isDisabled && onRemoveItem && (
            <Tooltip
              content={
                removeItemAriaLabel ||
                t('sales.form.product.remove_additional_item') ||
                'Remove item'
              }
              position="bottom"
            >
              <button
                type="button"
                aria-label={
                  removeItemAriaLabel ||
                  t('sales.form.product.remove_additional_item') ||
                  'Remove item'
                }
                onClick={onRemoveItem}
                className="ml-2 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {showOfferControls && !isDisabled && (
        <SaleOfferControl
          variant="rowAction"
          formOnly
          offer={offer}
          baseAmount={baseAmount}
          currency={currency}
          onChange={onOfferChange}
          isDisabled={offerDisabled}
          disabledHint={disabledHint}
          isExpanded={offerExpanded}
          onExpandedChange={handleOfferExpandedChange}
        />
      )}
    </li>
  );
}

export default SaleItemLine;
